import React, { useEffect, useMemo, useState } from 'react';
import {
  Flex,
  Text,
  Tooltip,
  Box,
  Skeleton,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react';
import {
  faClone,
  faDeleteRight,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonIcon,
  ButtonText,
  ClearButton,
  SearchInput
} from '@morressier/alembic';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchWorkspaceSubmissions,
  fetchWorkspaceTags,
  fetchSubmissionsDashBoardConfig,
  IWorkspaceSubmissionFilter
} from 'src/api/queries';
import { useForm } from 'react-hook-form';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import SubmissionLoader from '../../Submissions/SubmissionLoader';
import { SubmissionAuthors } from '../../Submissions/SubmissionAuthors';
import {
  ISubmissionStatus,
  SUBMISSION_STATUS_MAP,
  SUBMISSION_TYPE_MAP
} from 'src/constants';
import { IDocumentType } from '@publishing-engine/domain/models';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import { parseQueryParams, updateQueryParams } from 'src/utils/submissionsList';
import { formatDate } from 'src/utils/date';
import { filtersItem } from '../../Submissions';
import { AllArchivedSubmissionsProps } from './AllArchivedSubmissions';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

const defaultValues: IWorkspaceSubmissionFilter = {
  workflowPhase: [],
  journalId: [],
  documentType: [],
  assigneeId: [],
  tagId: [],
  searchParams: '',
  focusIssueId: []
};

const ACCEPTED_WORKFLOW_PHASES: any[] = ['accepted', 'formalAcceptance'];

const AcceptedSubmissions: React.FC<AllArchivedSubmissionsProps> = ({
  workspace,
  setSelectedSubmissionForClone
}) => {
  const { watch, reset, resetField, setValue, getValues } =
    useForm<IWorkspaceSubmissionFilter>({
      defaultValues
    });
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<SortField | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceId = workspace.id;

  const filters = watch();

  const view = 'archive';
  const { data: dashBoardConfigs } = useQuery({
    queryKey: ['submission-dashboard-config', { view, workspaceId }],
    queryFn: () => fetchSubmissionsDashBoardConfig({ view, workspaceId })
  });

  const archiveFilters = useMemo(() => {
    if (!dashBoardConfigs?.filters) return [];
    return dashBoardConfigs.filters.filter(
      (filter: any) => filter.key !== 'decisionType'
    );
  }, [dashBoardConfigs?.filters]);

  const { data: manageTags } = useQuery({
    queryKey: ['workspace-tags', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceTags(workspaceId) : null)
  });

  const offset = (page - 1) * pageSize;

  const { isLoading, isFetchingNextPage, data } = useInfiniteQuery({
    retry: 0,
    queryKey: [
      'submission-list-archive-accepted',
      {
        workspaceId,
        filters,
        page,
        pageSize,
        sort: sort !== null ? sort : undefined
      }
    ],
    enabled: !!dashBoardConfigs?.filters && !!workspaceId,
    queryFn: () => {
      const userTags = filters.tagId.filter(tagId =>
        manageTags?.collection.some(
          allTag => tagId === allTag.id && allTag.usage === 'users'
        )
      );
      const submissionTags = filters.tagId.filter(tagId =>
        manageTags?.collection.some(
          allTag => tagId === allTag.id && allTag.usage === 'submissions'
        )
      );
      return fetchWorkspaceSubmissions({
        workspaceId,
        limit: pageSize,
        offset,
        filters: {
          ...filters,
          tagId: submissionTags ?? [],
          userTags: userTags ?? [],
          workflowPhase: ACCEPTED_WORKFLOW_PHASES
        },
        sort: sort ?? undefined
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const maxOffset = lastPage.total;
      const nextOffset = allPages.length * pageSize;
      return nextOffset < maxOffset ? nextOffset : undefined;
    }
  });

  const clearFilters = () => {
    reset(defaultValues);
    setSearchValue('');
    setSelectedFilters({});
    setSort(null);
    navigate(location.pathname);
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        journalId: filters.journalId ?? [],
        documentType: filters.documentType ?? [],
        searchParams: (filters.searchParams ?? '').trim()
      }),
    [filters.journalId, filters.documentType, filters.searchParams]
  );
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  useEffect(() => {
    const { filters: newFilters, sort: newSort } = parseQueryParams(
      location.search
    );
    setSearchValue(newFilters.searchParams);
    setSort(newSort);
    reset(newFilters);
  }, [reset]);

  useEffect(() => {
    updateQueryParams(
      {
        ...filters,
        sort: sort ? `${sort.label},${sort.value},${sort.direction}` : ''
      },
      location,
      navigate
    );
  }, [filters, sort, navigate]);

  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      setValue(key as string, values as any);
    });
  };

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: filtersItem
  ) => {
    const currentFilter = getValues(fieldName);
    const updatedFilter = currentFilter.filter(f => f !== filterToRemove);
    setValue(fieldName, updatedFilter, {
      shouldValidate: true,
      shouldDirty: true
    });
    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
  };

  const total = data?.pages?.[data.pages.length - 1]?.total ?? 0;
  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, total);
  const hasFilters =
    !!filters.journalId.length ||
    !!filters?.searchParams ||
    !!filters.documentType.length;

  return (
    <>
      <Helmet>
        <title>Accepted for Publication</title>
      </Helmet>
      <Flex flexDirection={'column'} gap={4} width="100%">
        <Flex gap={2} flexDirection="column">
          <Flex gap={3} mb={2} direction={{ base: 'column', md: 'row' }}>
            <Flex gap={2} alignItems="center" flex="1">
              <SearchInput
                placeholder="Search submissions by title or manuscript ID"
                iconLeft={<FontAwesomeIcon icon={faSearch} />}
                width="100%"
                name="searchParams"
                value={searchValue}
                enterKeyHint="search"
                onChange={e => setSearchValue(e.currentTarget.value)}
                onInputClear={() => {
                  setSearchValue('');
                  resetField('searchParams');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setValue('searchParams', searchValue.trim());
                  }
                }}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setValue('searchParams', searchValue.trim());
                  }
                }}
              />
              <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                <Button
                  variant="primary"
                  aria-label="Search"
                  onClick={() => setValue('searchParams', searchValue.trim())}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </Box>
            </Flex>
            {archiveFilters.length > 0 && (
              <Box width={{ base: '100%', md: 'auto' }}>
                <FilterDrawer
                  filterGroups={archiveFilters.map((filter: any) => ({
                    ...filter,
                    items: filter.items ?? []
                  }))}
                  watch={watch}
                  onSubmit={handleFiltersSubmit}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              </Box>
            )}
            {dashBoardConfigs?.sortOptions && (
              <Box width={{ base: '100%', md: 'auto' }}>
                <SortMenu
                  appliedSort={sort}
                  options={dashBoardConfigs.sortOptions}
                  onApply={handleSortApply}
                  onReset={handleSortReset}
                />
              </Box>
            )}
          </Flex>
        </Flex>
        <Skeleton isLoaded={!isLoading}>
          <Flex
            alignItems={{ base: 'flex-start', lg: 'center' }}
            gap={3}
            height={{ lg: 5 }}
            flexDirection={{ base: 'column', lg: 'row' }}
            justifyContent={'space-between'}
            width="100%"
          >
            <Flex wrap="wrap" alignItems="center">
              <Text textStyle="textBody">
                {total > 0 ? (
                  <>
                    Showing <strong>{startIndex}</strong> –{' '}
                    <strong>{endIndex}</strong> of <strong>{total}</strong>{' '}
                    accepted submission{total === 1 ? '' : 's'}
                  </>
                ) : (
                  <>
                    <strong>0</strong> accepted submissions
                  </>
                )}
              </Text>
              {hasFilters && (
                <ButtonText
                  onClick={clearFilters}
                  leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                  variant="secondary"
                  marginX={{ base: 0, md: 2 }}
                >
                  Clear filters
                </ButtonText>
              )}
            </Flex>
          </Flex>
          {!!hasFilters && (
            <Flex alignItems="center" gap={2} wrap="wrap" marginTop={2}>
              {archiveFilters.map(filterConfig => {
                const hasItems = (fc: any): fc is { items: any[] } =>
                  Array.isArray(fc.items);
                if (
                  !hasItems(filterConfig) ||
                  filterConfig.items.length === 0
                ) {
                  return null;
                }
                const filterKey = filterConfig.key as keyof typeof filters;
                const filterValues = filters?.[filterKey];
                if (!Array.isArray(filterValues) || filterValues.length === 0) {
                  return null;
                }
                return filterValues.map(value => {
                  const item = filterConfig.items.find(
                    (i: any) => i.value === value
                  );
                  const label = item?.name ?? value;
                  return (
                    <Badge
                      key={`${filterKey}-${value}`}
                      size="base"
                      variant="interactive"
                    >
                      <Flex alignItems="center" gap={2}>
                        {label}
                        <FontAwesomeIcon
                          icon={faXmark}
                          cursor="pointer"
                          onClick={() =>
                            removeSingleFilter(value, filterKey as filtersItem)
                          }
                        />
                      </Flex>
                    </Badge>
                  );
                });
              })}
            </Flex>
          )}
        </Skeleton>
        <Box width="100%">
          {isLoading ? (
            <SubmissionLoader
              title="Loading Search Results"
              subTitle="We are retrieving your search results. This should only take a moment."
              bgColor="neutralSecondaryFill"
            />
          ) : data?.pages?.every(page => page?.items?.length !== 0) ? (
            <Box overflowY={'auto'} width="100%">
              {/* @ts-ignore */}
              <Table size={'sm'} minWidth="100%">
                <Thead>
                  <Tr
                    data-testId="accepted-table-header"
                    gap={4}
                    sx={{
                      '& th': {
                        textTransform: 'none',
                        color: 'black',
                        background: 'white'
                      }
                    }}
                  >
                    <Th p={4}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Title
                      </Text>
                    </Th>
                    <Th p={4}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Decision
                      </Text>
                    </Th>
                    <Th p={4}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Date of Decision
                      </Text>
                    </Th>
                    <Th p={4}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Journal
                      </Text>
                    </Th>
                    <Th p={4}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Article Type
                      </Text>
                    </Th>
                    {workspace.featureConfig?.cloneSubmission && (
                      <Th p={4} width={20}></Th>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.pages.map(page =>
                    page?.items.map(
                      ({
                        submission: { title, documentType, authors },
                        journal,
                        manuscriptId,
                        id,
                        submissionContextId,
                        workflow: { currentState, dateEnteredCurrentPhase }
                      }) => (
                        <Tr
                          key={id}
                          data-testId="accepted-table-row"
                          gap={4}
                          background="white"
                        >
                          <Td
                            onClick={() =>
                              navigate(
                                `/app/workspace/${
                                  location.pathname.split('/')[3]
                                }/j/${submissionContextId}/submissions?selectedItem=${id}`,
                                {
                                  state: {
                                    from: location.pathname + location.search
                                  }
                                }
                              )
                            }
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                          >
                            <ClearButton
                              width="100%"
                              ariaLabel={`Open submission ${title}`}
                              data-testId="submissions-row-title"
                            >
                              <Flex
                                direction="column"
                                alignItems="flex-start"
                                justifyContent="flex-start"
                                width="100%"
                                minWidth={0}
                                textAlign="left"
                              >
                                <Tooltip
                                  hasArrow
                                  borderRadius={4}
                                  placement="top"
                                  label={'View Submission'}
                                >
                                  <Box width="100%">
                                    <Text
                                      textStyle="headingLine"
                                      whiteSpace="normal"
                                    >
                                      {manuscriptId}
                                    </Text>
                                  </Box>
                                  <Box noOfLines={1} isTruncated width="100%">
                                    <Text
                                      textStyle="textBody"
                                      whiteSpace="normal"
                                      textDecoration="none"
                                      _hover={{
                                        textDecoration: 'underline',
                                        textDecorationColor: 'actionPrimaryFill'
                                      }}
                                    >
                                      <RichTextPreview
                                        value={title}
                                        color="actionPrimaryFill"
                                      />
                                    </Text>
                                  </Box>
                                </Tooltip>
                                <SubmissionAuthors authors={authors} />
                              </Flex>
                            </ClearButton>
                          </Td>
                          <Td textStyle="textBody">
                            <Badge
                              size="context"
                              variant={
                                SUBMISSION_STATUS_MAP[
                                  currentState as ISubmissionStatus
                                ]?.variant
                              }
                            >
                              {SUBMISSION_STATUS_MAP[
                                currentState as ISubmissionStatus
                              ]?.label ?? currentState}
                            </Badge>
                          </Td>
                          <Td textStyle="textBody">
                            {dateEnteredCurrentPhase
                              ? formatDate(new Date(dateEnteredCurrentPhase!))
                              : null}
                          </Td>
                          <Td textStyle="textBody" textTransform="uppercase">
                            {journal?.shortname}
                          </Td>
                          <Td textStyle="textBody">
                            {SUBMISSION_TYPE_MAP[
                              documentType as IDocumentType
                            ] ?? documentType}
                          </Td>
                          {workspace.featureConfig?.cloneSubmission && (
                            <Td textStyle="textBody">
                              <Flex justifyContent="flex-end">
                                <Box>
                                  <ButtonIcon
                                    variant="secondary"
                                    ariaLabel={`Clone submission ${
                                      manuscriptId ?? id
                                    }`}
                                    icon={<FontAwesomeIcon icon={faClone} />}
                                    onClick={() =>
                                      setSelectedSubmissionForClone({
                                        manuscriptId: manuscriptId ?? id,
                                        sourceSubmissionId: id,
                                        submissionContextId
                                      })
                                    }
                                  />
                                </Box>
                              </Flex>
                            </Td>
                          )}
                        </Tr>
                      )
                    )
                  )}
                  {isFetchingNextPage && (
                    <Tr>
                      <Td colSpan={5} textAlign="center">
                        <Text>Loading more...</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <SubmissionLoader
              title="No Results Found"
              subTitle="We could not find any results matching your search criteria. Please adjust your search and try again."
              bgColor="neutralSecondaryFill"
            />
          )}
          <TablePagination
            ariaLabel="Accepted submissions pagination"
            currentPage={page}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </Box>
      </Flex>
    </>
  );
};

export default AcceptedSubmissions;
